import type { ReactNode } from "react";
import { BluebirdLayout } from "@/components/bluebird/BluebirdLayout";
import { BreadcrumbJsonLd } from "@/components/shared/json-ld";
import { SlimFooter, SubHero, SubMasthead } from "@/components/bluebird/Chrome";
import { BrandFilter, BikeSection, type CatalogBrand } from "@/components/bluebird/Catalog";
import { SavingsCallout } from "@/components/bluebird/SavingsCallout";
import { color, maxWidth, space } from "@/lib/design-tokens";
import { bikes as copy } from "@/lib/copy/bikes";
import { shop } from "@/lib/copy/shop";
import type { Bike } from "@/types/bike";
import { CatalogNotice, type CatalogStatus } from "@/components/bluebird/CatalogNotice";

interface BrandGroup extends CatalogBrand {
  bikes: Bike[];
}

interface Props {
  brand_groups?: BrandGroup[];
  brands?: CatalogBrand[];
  current_brands?: string[];
  past_models?: Bike[];
  total_in_stock?: number;
  catalog_status?: CatalogStatus;
}

/**
 * `/bikes` is the only door.
 *
 * Brand is the only active facet because it is a real catalog field. The page
 * does not infer use-case, motor or capacity from product titles. Bikes that
 * are gone are kept, demoted below the in-stock brand groups, and badged.
 */
function BikesIndex({
  brand_groups = [],
  brands = [],
  current_brands = [],
  past_models = [],
  catalog_status,
}: Props) {
  const nothingInStock = brand_groups.length === 0;

  return (
    <>
      {/* Structured data only — renders no visible markup, so it cannot move a
          pixel in script/design_diff.mjs. Kept from the previous design because
          breadcrumbs are what let a search result show the path to the page. */}
      <BreadcrumbJsonLd items={[{ name: "Bikes", href: "/bikes" }]} />

      <SubMasthead
        current="/bikes"
        photo="hero-bikes"
        photoAlt="A parent riding a longtail cargo bike with two children on the back"
      >
        <SubHero
          line1={copy.heroLine1}
          line2={copy.heroLine2}
          intro={copy.heroIntro}
          introMaxWidth="620px"
        />
      </SubMasthead>

      {/* The <main> landmark. A screen-reader user's "skip to content" jump
          lands here, and without it the only landmarks on the page are a banner
          and a footer with everything else unlabelled. <main> is display:block
          with no margin, so it wraps the same boxes at the same positions —
          verified by script/design_diff.mjs, which still reports 0%. */}
      <main>
      <CatalogNotice status={catalog_status} />

      <BrandFilter brands={brands} currentBrands={current_brands} />

      {nothingInStock && (
        <section
          data-section="empty"
          style={{
            maxWidth,
            margin: "0 auto",
            padding: `${space.sectionY} ${space.gutter}`,
            fontSize: "19px",
            color: color.inkMuted,
          }}
        >
          {/* Never a silently blank grid — the failure mode the research found
              on a peer shop's cargo facet. Say what happened and give a way
              through. */}
          {current_brands.length > 0 ? (
            <>
              {copy.emptyFiltered}{" "}
              <a href="/bikes" className="bb-underline-grow" style={{ fontWeight: 700 }}>Clear filters</a>
            </>
          ) : (
            <>
              {copy.emptyState}{" "}
              <a href={shop.phoneHref} className="bb-underline-grow" style={{ fontWeight: 700 }}>{shop.phone}</a>
            </>
          )}
        </section>
      )}

      {brand_groups.map((group, index) => (
        <BikeSection
          key={group.slug}
          id={`brand-${group.slug}`}
          brandSlug={group.slug}
          heading={group.name}
          bikes={group.bikes}
          style={index === 0 ? { padding: `0 ${space.gutter} ${space.sectionY}` } : undefined}
        />
      ))}
      <SavingsCallout />
      {past_models.length > 0 && <BikeSection
        id="gone"
        heading={copy.pastModelsHeading}
        blurb={copy.pastModelsBlurb}
        bikes={past_models}
        style={{ paddingTop: space.sectionY }}
      />}

      </main>

      <SlimFooter borderTop />
    </>
  );
}

BikesIndex.layout = (page: ReactNode) => <BluebirdLayout>{page}</BluebirdLayout>;

export default BikesIndex;
